import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../apiClient';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export type LogoStyle = 'transparent' | 'light_plate' | 'dark_plate' | 'auto_contrast';

export interface ProductBranding {
  id: string;
  logoUrl: string | null;
  logoStoragePath: string | null;
  brandColor: string | null;
  logoStyle: LogoStyle;
  systemRequirementsMd: string | null;
  releaseNotesFieldId: number | null;
}

interface ProductsListRow {
  id: string;
  logo_url: string | null;
  logo_storage_path: string | null;
  brand_color: string | null;
  logo_style: LogoStyle;
  system_requirements_md: string | null;
  release_notes_field_id: number | null;
  [k: string]: unknown;
}

const PRODUCTS_KEY = ['/api/products'] as const;

function mapRow(row: ProductsListRow): ProductBranding {
  return {
    id: row.id,
    logoUrl: row.logo_url ?? null,
    logoStoragePath: row.logo_storage_path ?? null,
    brandColor: row.brand_color ?? null,
    logoStyle: (row.logo_style ?? 'auto_contrast') as LogoStyle,
    systemRequirementsMd: row.system_requirements_md ?? null,
    releaseNotesFieldId: row.release_notes_field_id ?? null,
  };
}

// ----------------------------------------------------------------------------
// useProductBranding
//
// Backed by the existing GET /api/products list endpoint (single source of
// truth for product fields). The hook selects only branding fields for the
// requested productId. Cache is shared with any other consumer of `/api/products`,
// so updating branding invalidates the list and refetches once.
// ----------------------------------------------------------------------------

export function useProductBranding(productId: string | null | undefined) {
  return useQuery<ProductBranding | null, Error>({
    queryKey: [...PRODUCTS_KEY, 'branding', productId ?? null],
    queryFn: async ({ signal }) => {
      const res = await apiFetch('/api/products', { signal });
      if (!res.ok) throw new Error(`API ${res.status}: /api/products`);
      const json: { products?: ProductsListRow[] } = await res.json();
      const row = (json.products || []).find((p) => p.id === productId);
      return row ? mapRow(row) : null;
    },
    enabled: !!productId,
    staleTime: 60_000,
  });
}

// ----------------------------------------------------------------------------
// useUpdateProductBranding
// ----------------------------------------------------------------------------

export interface UpdateProductBrandingVars {
  productId: string;
  brandColor?: string | null;
  logoStyle?: LogoStyle;
  systemRequirementsMd?: string | null;
  releaseNotesFieldId?: number | null;
}

export function useUpdateProductBranding() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, UpdateProductBrandingVars>({
    mutationFn: async ({ productId, ...rest }) => {
      // Map camelCase -> snake_case to match server PATCH body schema.
      const body: Record<string, unknown> = {};
      if (rest.brandColor !== undefined) body.brand_color = rest.brandColor;
      if (rest.logoStyle !== undefined) body.logo_style = rest.logoStyle;
      if (rest.systemRequirementsMd !== undefined)
        body.system_requirements_md = rest.systemRequirementsMd;
      if (rest.releaseNotesFieldId !== undefined)
        body.release_notes_field_id = rest.releaseNotesFieldId;
      const res = await apiFetch(`/api/products/${encodeURIComponent(productId)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text || `/api/products/${productId}`}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
    },
  });
}

// ----------------------------------------------------------------------------
// useUploadProductLogo
//
// Browser sets Content-Type with multipart boundary automatically when given
// a FormData body. apiFetch leaves Content-Type unset for non-string bodies.
// ----------------------------------------------------------------------------

export interface UploadProductLogoVars {
  productId: string;
  file: File | Blob;
  filename?: string;
}

export interface UploadProductLogoResponse {
  id: string;
  logoUrl: string | null;
  logoStoragePath: string | null;
}

export function useUploadProductLogo() {
  const queryClient = useQueryClient();
  return useMutation<UploadProductLogoResponse, Error, UploadProductLogoVars>({
    mutationFn: async ({ productId, file, filename }) => {
      const form = new FormData();
      form.append('logo', file, filename || (file instanceof File ? file.name : 'logo'));
      const res = await apiFetch(`/api/products/${encodeURIComponent(productId)}/logo`, {
        method: 'POST',
        // FormData -> let the browser set Content-Type with boundary.
        body: form as unknown as BodyInit,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text || `/api/products/${productId}/logo`}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
    },
  });
}

// ----------------------------------------------------------------------------
// useDeleteProductLogo
// ----------------------------------------------------------------------------

export function useDeleteProductLogo() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, { productId: string }>({
    mutationFn: async ({ productId }) => {
      const res = await apiFetch(`/api/products/${encodeURIComponent(productId)}/logo`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text || `/api/products/${productId}/logo`}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
    },
  });
}
